import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";

import ProctoringAdminService from "../../_services/ProctoringAdminService";
import WebProctoringPanel from "../quick-quiz-maker/WebProctoringPanel";

/**
 * Webcam tab of the Proctoring Center — the webcam-AI evidence roster for a
 * test. Lists every student who launched (from
 * student_online_test_partial_submit), with the AI violation counters, capture
 * counts, and DynamoDB block flags — no browser violation or block needed to
 * get here. Row drill-in opens the full WebProctoringPanel (photos, metrics,
 * Test/Blanket Block levers).
 */

interface RosterRow {
  enquiryId: number;
  userId: number;
  studentName: string;
  lastLaunch: string | null;
  submitted: boolean;
  webcamOn: boolean;
  aiViolations: {
    authenticateFace: number;
    mobile: number;
    secondFace: number;
    zeroFace: number;
  };
  captures: number;
  hasProctorDoc: boolean;
  blockTest: boolean;
  blanketFlag: string;
}

const chip = (bg: string, color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

const WebcamPanel: React.FC = () => {
  const [testIdInput, setTestIdInput] = useState(
    new URLSearchParams(window.location.search).get("testId") || "",
  );
  const [testName, setTestName] = useState("");
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<RosterRow | null>(null);

  const fetchRoster = useCallback(async () => {
    const testId = Number(testIdInput);
    if (!testId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ProctoringAdminService.getWebcamRoster(testId);
      const d = res.data.data;
      setRoster(d.roster || []);
      setTestName(d.testName || "");
      setTruncated(!!d.truncated);
      setLoaded(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          "Failed to load the roster",
      );
    } finally {
      setLoading(false);
    }
  }, [testIdInput]);

  useEffect(() => {
    if (testIdInput) fetchRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (detailTarget) {
    return (
      <WebProctoringPanel
        testId={Number(testIdInput)}
        enquiryId={detailTarget.enquiryId}
        onBack={() => setDetailTarget(null)}
      />
    );
  }

  const totalAi = (r: RosterRow) =>
    r.aiViolations.authenticateFace +
    r.aiViolations.mobile +
    r.aiViolations.secondFace +
    r.aiViolations.zeroFace;

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <h5 style={{ margin: 0 }}>
          Webcam Evidence{testName ? ` — ${testName}` : ""}
        </h5>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            placeholder="Test ID"
            value={testIdInput}
            onChange={(e) => setTestIdInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchRoster()}
          />
          <Button size="sm" variant="primary" onClick={fetchRoster} disabled={loading}>
            Load
          </Button>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
        Every student who launched the test, with their webcam-AI summary —
        View opens the capture photos, per-image metrics, and the Test Block /
        Blanket Block controls.
      </p>

      {error && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 12,
            fontSize: 13,
            color: "var(--status-danger)",
            background: "var(--status-danger-bg)",
            border: "1px solid var(--status-danger-border)",
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      )}
      {truncated && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
          Showing the first 500 students — narrow down by test if you need the rest.
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner animation="border" size="sm" /> Loading roster...
        </div>
      ) : !loaded ? (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "var(--text-muted)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          Enter a Test ID to see everyone who launched it and their webcam
          evidence.
        </div>
      ) : roster.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "var(--text-muted)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          Nobody has launched this test yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table striped bordered hover size="sm" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Student</th>
                <th>Last Launch</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "center" }}>Captures</th>
                <th style={{ textAlign: "center" }}>AI Violations</th>
                <th style={{ textAlign: "center" }}>Blocks</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((r) => (
                <tr key={r.enquiryId}>
                  <td>{r.studentName}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {r.lastLaunch ? new Date(r.lastLaunch).toLocaleString() : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {r.submitted ? (
                      <span style={chip("#dcfce7", "#15803d")}>Submitted</span>
                    ) : (
                      <span style={chip("#e0f2fe", "#0369a1")}>In Progress</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {r.hasProctorDoc ? (
                      r.captures
                    ) : (
                      <span style={{ color: "var(--text-faint)" }} title="No proctoring document — webcam capture may not have started">
                        —
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {totalAi(r) > 0 ? (
                      <span
                        style={chip("#fee2e2", "#b91c1c")}
                        title={`Face auth: ${r.aiViolations.authenticateFace}, Mobile: ${r.aiViolations.mobile}, Second face: ${r.aiViolations.secondFace}, No face: ${r.aiViolations.zeroFace}`}
                      >
                        {totalAi(r)}
                      </span>
                    ) : (
                      <span style={chip("#dcfce7", "#15803d")}>0</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    {r.blockTest && (
                      <span style={{ ...chip("#fee2e2", "#b91c1c"), marginRight: 4 }}>
                        Test
                      </span>
                    )}
                    {r.blanketFlag === "BLOCK" && (
                      <span style={chip("#fee2e2", "#b91c1c")}>Blanket</span>
                    )}
                    {!r.blockTest && r.blanketFlag !== "BLOCK" && (
                      <span style={{ color: "var(--text-faint)" }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Button
                      size="sm"
                      variant="primary"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      onClick={() => setDetailTarget(r)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default WebcamPanel;
